import PropTypes from 'prop-types';
// @flow

import React, { Component } from 'react';
import _ from 'lodash'

import I18n from 'PMT/i18n/I18n';

// -- constants
import Events from 'constants/Events';
import RoutingEnum from 'constants/RoutingEnum';

// -- utils
import ViewUtils from 'PMT/utils/ViewUtils';

import Listener from 'PMT/flux/Listener';

import SecurityContext from 'utils/permissions/SecurityContext';


// -- actions
import { RestaurantMenuAction, RouteAction } from 'actions';

// -- stores
import SessionStore from 'stores/SessionStore';
import {RestaurantMenuStore, OrderSettingsStore, RestaurantStore} from 'stores';

// -- entities
import Restaurant from 'PMT/entities/Restaurant';
import {
  Product,
  RestaurantMenu
} from 'PMT/entities/menu';

import { OrderSettings } from 'PMT/entities/order';

// -- components
import RestaurantMenuTabs from "pages/menu/RestaurantMenuTabs";
import AbstractComponent from 'components/AbstractComponent';
import Ariane from 'PMT/components/Ariane';
import ProductEditView from './ProductEditView';

export default class ProductEditPage extends AbstractComponent {

  state = {
    restaurantMenu: RestaurantMenuStore.getRestaurantMenu(),
    restaurant: RestaurantStore.getRestaurant(),
    product: this.isEditMode() ? null : new Product(),
    apiError: null
  };

  componentDidMount() {
    RestaurantMenuStore.addListeners(this.restaurantMenuStoreListeners);

    if (!SecurityContext.restaurantMenu.canEdit) {
      // TODO: redirect
    }

    if (_.isNull(this.state.restaurantMenu)) {
      RestaurantMenuAction.loadRestaurantMenu();
    } else if (this.isEditMode()) {
      this.loadProduct();
    }
  }

  componentWillUnmount() {
    RestaurantMenuStore.removeListeners(this.restaurantMenuStoreListeners);
  }

  get restaurantMenuStoreListeners(): Array<Listener> {
    return [
      new Listener(Events.ON_LOADING_RESTAURANT_MENU_SUCCESS, this.onChange),
      new Listener(Events.ON_LOADING_RESTAURANT_MENU_ERROR, this.onError),

      new Listener(Events.ON_UPDATE_RESTAURANT_MENU_SUCCESS, this.onProductUpdated),
      new Listener(Events.ON_UPDATE_RESTAURANT_MENU_ERROR, this.onError),

      new Listener(Events.ON_UPDATE_RESTAURANT_MENU_PRODUCT_SUCCESS, this.onProductUpdated),
      new Listener(Events.ON_UPDATE_RESTAURANT_MENU_PRODUCT_ERROR, this.onError),

      new Listener(Events.ON_DELETE_RESTAURANT_MENU_PRODUCT_IMAGE_RESPONSE_SUCCESS, this.onProductUpdated),
      new Listener(Events.ON_DELETE_RESTAURANT_MENU_PRODUCT_IMAGE_RESPONSE_ERROR, this.onError),

      new Listener(Events.ON_CREATE_RESTAURANT_MENU_PRODUCT_SUCCESS, this.onProductCreated),
      new Listener(Events.ON_CREATE_RESTAURANT_MENU_PRODUCT_ERROR, this.onError),

      new Listener(Events.ON_DELETE_RESTAURANT_MENU_PRODUCT_SUCCESS, this.onProductDeleted),
      new Listener(Events.ON_DELETE_RESTAURANT_MENU_PRODUCT_ERROR, this.onError),
    ];
  }

  loadProduct() {
    const product:Product = this.getProductOnRestaurantMenu(this.state.restaurantMenu);

    // TODO: if product === null redirect to 404.
    this.setState({
      product,
      loading: false
    });
  }

  // -- Event functions

  onChange = () => {
    const restaurantMenu: RestaurantMenu = RestaurantMenuStore.getRestaurantMenu();

    if (this.isEditMode()) {
      const product: Product = this.getProductOnRestaurantMenu(restaurantMenu);
      this.setState({
        restaurantMenu,
        product,
        loading: false
      });
    } else {
      this.setState({
        restaurantMenu,
        loading: false
      });
    }
  };

  onProductUpdated = () => {
    if (this.isEditMode()) {
      const restaurantMenu: RestaurantMenu = RestaurantMenuStore.getRestaurantMenu();
      const product: Product = this.getProductOnRestaurantMenu(restaurantMenu);

      this.setState({
        restaurantMenu,
        product,
        loading: false
      });
    }
  };

  onProductCreated = () => {
    const restaurantMenu: RestaurantMenu = RestaurantMenuStore.getRestaurantMenu();
    const product: Product = RestaurantMenuStore.getCreatedProduct();
    RouteAction.presentProductEdit(product);

    this.setState({
      restaurantMenu,
      product,
      loading: false
    });
  };

  onProductDeleted = () => {
    RouteAction.presentProductsTab();
  };

  onError = () => {
    this.setState({
      apiError: RestaurantMenuStore.getApiError()
    });
  };

  onUpdateProduct = (product: Product, image: ?DataUri) => {
    this.showLoading();
    const imageFile = _.isNull(image) ? null : ViewUtils.dataUriToFile(image);
    RestaurantMenuAction.saveProduct(product, imageFile);
  };

  onImageRemove = (product: Product) => {
    this.showLoading();
    RestaurantMenuAction.deleteProductImage(product);
  }

  onCreateProduct = (product: Product, image: ?DataUri) => {
    this.showLoading();
    const imageFile = _.isNull(image) ? null : ViewUtils.dataUriToFile(image);
    RestaurantMenuAction.createProduct(product, imageFile);
  };

  onDeleteProduct = (product: Product) => {
    // TODO
    this.showLoading();
    RestaurantMenuAction.deleteProduct(product);
  };

  get product(): Product {
    return this.state.product;
  }

  get restaurantMenu(): RestaurantMenu {
    return this.state.restaurantMenu;
  }

  isEditMode() {
    return !_.isUndefined(this.props.match.params.productId);
  }

  getProductOnRestaurantMenu(restaurantMenu) {
    const productId = this.props.match.params.productId;
    const product:Product = restaurantMenu.getProductWithId(productId);

    return product;
  }


  // -- renderers

  renderBody() {
    if (!_.isNull(this.apiError)) {
      return this.renderApiErrorBlock();
    } else if (_.isNull(this.product)) {
      return this.renderLoading(true);
    }

    const arianeIcon = 'fa-shopping-cart';

    const arianeDetails = [
      {
        name: I18n.tr('menu.order'),
        link: null
      },
      {
        name: I18n.tr('menu.order.restaurant_menu'),
        link: RoutingEnum.RESTAURANT_MENU,
        query: {
          tab: RestaurantMenuTabs.CATEGORIES
        }
      },
      {
        name: I18n.tr('menu.order.restaurant_menu.products'),
        link: RoutingEnum.RESTAURANT_MENU,
        query: {
          tab: RestaurantMenuTabs.PRODUCTS
        }
      }
    ];

    if (this.isEditMode()) {
      arianeDetails.push({
        name: this.product.name,
        link: RoutingEnum.RESTAURANT_MENU_PRODUCT_DETAIL,
        params: {
          productId: this.product.id
        }
      });
      arianeDetails.push({
        name: I18n.tr('menu.order.restaurant_menu.edition'),
        link: null
      });
    } else {
      arianeDetails.push({
        name: I18n.tr('menu.order.restaurant_menu.new'),
        link: null
      });
    }

    return (
      <div>
        <Ariane icon={arianeIcon} details={arianeDetails} />

        <ProductEditView
          product={this.product}
          restaurantMenu={this.restaurantMenu}
          onUpdate={this.onUpdateProduct}
          onImageRemove={this.onImageRemove}
          onCreate={this.onCreateProduct}
          onDelete={this.onDeleteProduct}
          isEditMode={this.isEditMode()}
          orderSettings={OrderSettingsStore.getOrderSettings()}
          currencyCode={this.state.restaurant.currency}
        />
      </div>
    );
  }

  render() {
    try {
      return (
        <div className="product_detail_page">
          {this.renderBody()}
          {this.renderLoading()}
        </div>
      );
    } catch (e) { console.error(e); }
  }

}
